/**
 * Compiles the vendored OCPP 2.1 JSON Schemas (draft-06, like 2.0.1) with Ajv
 * and exposes a per-action `validatorFor`, mirroring `protocol/v201/schemas.ts`.
 *
 * The 2.1 bundle is ~90 actions (181 files). Rather than hand-import each, the
 * registry is built from a Vite `import.meta.glob` over `schemas/ocpp21/*.json`
 * (eager so validation stays synchronous). Files are keyed by action name +
 * Request/Response suffix; non-conforming names (e.g. NotifyPeriodicEventStream
 * with no suffix) are skipped — those 2.1-only streaming messages are wired
 * when their feature lands.
 *
 * A fresh Ajv (not v16's draft-04 nor a shared instance) keeps the draft-06
 * metaschema isolated, exactly as v201 does.
 */
import Ajv from 'ajv';
import addFormats from 'ajv-formats';
import draft06 from 'ajv/dist/refs/json-schema-draft-06.json';
import { relaxOcppUriFormats } from '@/protocol/ocppUriFormat';

export type Direction = 'request' | 'response';
export type Validator = (data: unknown) => { valid: boolean; errors: readonly string[] };

const ajv = new Ajv({ allErrors: true, strict: false });
ajv.addMetaSchema(draft06);
addFormats(ajv);
relaxOcppUriFormats(ajv);

// Eager glob → { '/abs/.../AuthorizeRequest.json': { default: <schema> }, … }
const modules = import.meta.glob<{ default: unknown }>('../../../schemas/ocpp21/*.json', {
  eager: true,
});

const SCHEMAS: Record<string, { request?: unknown; response?: unknown }> = {};
for (const [filePath, mod] of Object.entries(modules)) {
  const file = filePath.split('/').pop() ?? '';
  if (file === 'pin.json') continue;
  const m = /^(.+?)(Request|Response)\.json$/.exec(file);
  if (!m) continue; // e.g. NotifyPeriodicEventStream.json — handled with its feature
  const action = m[1] as string;
  const direction = m[2]?.toLowerCase() as Direction;
  (SCHEMAS[action] ??= {})[direction] = mod.default;
}

const VALIDATORS = new Map<string, Validator>();

function compile(action: string, direction: Direction): Validator {
  const schema = SCHEMAS[action]?.[direction];
  if (schema === undefined) {
    return () => ({ valid: false, errors: [`unknown action "${action}"`] });
  }
  const compiled = ajv.compile(schema as Record<string, unknown>);
  return (data) => {
    const ok = compiled(data);
    if (ok) return { valid: true, errors: [] };
    const errors = (compiled.errors ?? []).map(
      (e) => `${e.instancePath || '<root>'} ${e.message ?? 'unknown'}`,
    );
    return { valid: false, errors };
  };
}

export function validatorFor(action: string, direction: Direction): Validator {
  const key = `${action}|${direction}`;
  let v = VALIDATORS.get(key);
  if (!v) {
    v = compile(action, direction);
    VALIDATORS.set(key, v);
  }
  return v;
}

export function listActions(): readonly string[] {
  return Object.keys(SCHEMAS).sort();
}
